import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash, MapPin, ShareNetwork, SealCheck, UserCheck, Target, Crown, Download, WhatsappLogo, XLogo, PencilSimple, Clock, Warning, Archive, Sparkle } from "@phosphor-icons/react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import ShotMap from "@/components/ShotMap";
import { AttesterPicker } from "@/components/AttesterPicker";
import { EditScorecardModal } from "@/components/EditScorecardModal";

export default function RoundDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [attestations, setAttestations] = useState({ incoming: [], outgoing: [] });
  const [picker, setPicker] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [shots, setShots] = useState([]);
  const [course, setCourse] = useState(null);
  // Bula Banter — AI-generated playful commentary on the round. Cached
  // server-side after first generation; the button re-shows the cached text
  // instantly on subsequent visits.
  const [banter, setBanter] = useState(null);
  const [banterLoading, setBanterLoading] = useState(false);
  const banterTriedAuto = useRef(false);
  const banterCardRef = useRef(null);
  const [banterShareBusy, setBanterShareBusy] = useState(false);

  // Live-update the edit-window countdown every 15 seconds.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const res = await api.get(`/rounds/${id}`);
      setR(res.data);
    } catch {
      navigate("/rounds");
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => {
    // Hydrate cached banter on load so re-opens are instant.
    if (r?.banter_text) {
      setBanter(r.banter_text);
      return;
    }
    // Auto-generate banter the first time the owner opens a fresh round —
    // way more discoverable than waiting for a button click. Owner-only +
    // never block the page load on it.
    if (r && user && r.user_id === user.id && !r.banter_text && !banterLoading && !banterTriedAuto.current) {
      banterTriedAuto.current = true;
      setBanterLoading(true);
      api.post(`/rounds/${id}/banter`)
        .then((res) => setBanter(res.data.banter))
        .catch(() => { /* silent — user can hit Generate button */ })
        .finally(() => setBanterLoading(false));
    }
  }, [r, user, id]); // eslint-disable-line
  useEffect(() => {
    api.get("/attestations").then((res) => setAttestations(res.data)).catch(() => {});
  }, []);

  // Pro-only: load this round's tracked shots + the course (for green coords).
  useEffect(() => {
    if (!r || !user?.is_pro) return;
    if (r.user_id !== user.id) return;  // owner only
    api.get(`/rounds/${id}/shots`).then((res) => setShots(res.data)).catch(() => {});
    api.get(`/courses/${r.course_id}`).then((res) => setCourse(res.data)).catch(() => {});
  }, [r, user, id]);

  // Group shots by hole, sorted by shot_index.
  const shotsByHole = useMemo(() => {
    const m = {};
    shots.forEach((s) => {
      (m[s.hole] = m[s.hole] || []).push(s);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.shot_index - b.shot_index));
    return m;
  }, [shots]);
  const holesWithShots = useMemo(() =>
    Object.keys(shotsByHole).map((n) => Number(n)).sort((a, b) => a - b),
  [shotsByHole]);

  if (!r) return <div className="py-10">Loading…</div>;

  const myAttestation = attestations.outgoing.find((a) => a.round_id === id && a.status !== "revoked");
  const isOwner = r.user_id === user?.id;
  const front = r.holes.slice(0, 9);
  const back = r.holes.slice(9);
  const sr = r.strokes_received || [];
  const sf = r.stableford || [];
  const sum = (arr, k) => arr.reduce((a, b) => a + (b[k] || 0), 0);

  // --- Editability ---
  const editableUntil = r.editable_until ? new Date(r.editable_until).getTime() : 0;
  const remainingMs = editableUntil - now;
  const isLegacy = r.attester_status === "legacy";
  const isAttested = r.attester_status === "accepted";
  const canEdit = isOwner && !isLegacy && !isAttested && remainingMs > 0;
  const editMinutesLeft = Math.max(0, Math.floor(remainingMs / 60000));

  const del = async () => {
    if (!window.confirm("Delete this round?")) return;
    try {
      await api.delete(`/rounds/${id}`);
      toast.success("Round deleted");
      navigate("/rounds");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const requestAttest = async (attesterId) => {
    try {
      await api.post(`/rounds/${id}/attest/request`, { attester_user_id: attesterId });
      toast.success("Attestation requested");
      setPicker(false);
      const [aRes] = await Promise.all([api.get("/attestations"), load()]);
      setAttestations(aRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const hasHandicap = r.course_handicap != null;

  // Status badge + helper text driven by attester_status.
  const statusBadge = (() => {
    if (isAttested) {
      return { cls: "bg-[#1E5631]/10 text-[#1E5631]", icon: <SealCheck size={14} weight="fill" />, label: `Attested by ${r.attested_by_name}` };
    }
    if (isLegacy) {
      return { cls: "bg-[#0F2537]/10 text-[#0F2537]/70", icon: <Archive size={14} weight="fill" />, label: "Legacy round (not attested)" };
    }
    if (r.attester_status === "pending") {
      return { cls: "bg-[#FFC72C]/15 text-[#0F2537]", icon: <Clock size={14} weight="fill" />, label: `Pending attestation from ${r.attester_name}` };
    }
    if (r.attester_status === "rejected") {
      return { cls: "bg-red-100 text-red-700", icon: <Warning size={14} weight="fill" />, label: "Rejected — pick a different attester" };
    }
    return null;
  })();

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs label-eyebrow flex items-center gap-1"><MapPin size={12} weight="fill" /> {r.course_name} · {r.tees}</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            {new Date(r.played_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </h1>
          {statusBadge && (
            <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full ${statusBadge.cls}`} data-testid="round-status-badge">
              {statusBadge.icon} {statusBadge.label}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <button onClick={() => setEditOpen(true)} data-testid="edit-round-btn"
              className="px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] hover:border-[#E05D3A] hover:text-[#E05D3A] inline-flex items-center gap-1 text-sm font-semibold">
              <PencilSimple size={14} /> Edit scorecard
            </button>
          )}
          <Link to={`/share/round/${id}`} target="_blank" data-testid="share-round-btn"
            className="px-3 py-2 rounded-lg bg-[#0F2537] text-white inline-flex items-center gap-1 text-sm font-semibold hover:brightness-110">
            <ShareNetwork size={14} weight="bold" /> Share
          </Link>
          {isOwner && !isLegacy && !isAttested && (
            <button onClick={del} data-testid="delete-round-btn" className="px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537]/70 hover:text-red-600 inline-flex items-center gap-1 text-sm font-semibold">
              <Trash size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Edit-window countdown (only while inside the window) */}
      {isOwner && canEdit && (
        <div className="rounded-xl bg-[#FFC72C]/15 border border-[#FFC72C]/40 px-4 py-2.5 text-xs flex items-center gap-2" data-testid="edit-window-banner">
          <Clock size={14} weight="fill" className="text-[#0F2537]" />
          <span className="text-[#0F2537]/80">
            <strong>{editMinutesLeft} min left</strong> to edit this card. After the window closes, the scorecard locks.
          </span>
        </div>
      )}

      {/* Attestation widget */}
      {isOwner && !isAttested && !isLegacy && (
        <div className="card-default p-5" data-testid="attestation-widget">
          <span className="label-eyebrow">Attestation</span>
          <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm text-[#0F2537]/80 max-w-md">
              {r.attester_status === "pending" && (
                <>Waiting for <strong>{r.attester_name}</strong> to sign your card. They'll get a notification.</>
              )}
              {r.attester_status === "rejected" && (
                <><strong>Rejected.</strong> Your previous attester declined — pick someone else.</>
              )}
              {r.attester_status === "none" && (
                <>Pick any registered golfer as your marker to make this round official.</>
              )}
            </div>
            {r.attester_status !== "pending" && (
              <button onClick={() => setPicker(true)} data-testid="request-attest-btn"
                className="px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold inline-flex items-center gap-1 hover:brightness-110">
                <UserCheck size={14} weight="bold" /> {r.attester_status === "rejected" ? "Pick someone else" : "Add attester"}
              </button>
            )}
          </div>
        </div>
      )}

      <AttesterPicker
        open={picker}
        onClose={() => setPicker(false)}
        onSelect={(uid) => requestAttest(uid)}
        currentUserId={user?.id}
      />

      <EditScorecardModal
        open={editOpen}
        round={r}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Strokes" v={r.total_strokes} accent />
        <Stat label="vs Par" v={`${r.total_strokes - r.total_par >= 0 ? "+" : ""}${r.total_strokes - r.total_par}`} />
        <Stat label="Differential" v={`${r.differential > 0 ? "+" : ""}${r.differential}`} />
        <Stat label="Stableford" v={r.total_stableford ?? "—"} />
      </div>

      {hasHandicap && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Course Handicap" v={r.course_handicap} />
          <Stat label="Playing Handicap" v={r.playing_handicap ?? "—"} />
          <Stat label="Net Score" v={r.total_net ?? "—"} />
          <Stat label="Index at Play" v={r.handicap_index_at_play?.toFixed(1) ?? "—"} />
        </div>
      )}

      {r.target_score && (
        <div className="card-default p-4 flex items-center gap-3 border-l-4 border-l-[#E05D3A]" data-testid="round-goal-summary">
          <Target weight="fill" size={20} className="text-[#E05D3A] shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/60">Goal Round</div>
            <div className="font-bold text-[#0F2537]">
              Target {r.target_score} ·{" "}
              {r.total_strokes < r.target_score
                ? <span className="text-[#1E5631]">Hit it — {r.target_score - r.total_strokes} under target!</span>
                : <span className="text-[#E05D3A]">Missed by {r.total_strokes - r.target_score}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Bula Banter — playful AI commentary (auto-generates on first open) */}
      {r.user_id === user?.id && (
        <div className="space-y-2" data-testid="banter-card">
          <div ref={banterCardRef} className="card-default p-0 overflow-hidden relative">
            {/* Decorative gradient + grain for shareable export */}
            <div className="relative px-5 pt-5 pb-4 bg-gradient-to-br from-[#0F2537] via-[#173049] to-[#1E5631] text-white overflow-hidden">
              <div className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 25% 30%, rgba(255,199,44,0.6), transparent 45%), radial-gradient(circle at 75% 70%, rgba(224,93,58,0.55), transparent 50%)" }} />
              <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#FFC72C]/95 text-[#0F2537] flex items-center justify-center shrink-0">
                  <Sparkle weight="fill" size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#FFC72C]">Bula Banter</div>
                  <div className="font-heading font-black text-lg leading-tight">CHIP-the-Coconut says…</div>
                </div>
              </div>
            </div>
            <div className="px-5 py-5 bg-[#F8F7F3]">
              {banter ? (
                <p className="text-sm text-[#0F2537] leading-relaxed whitespace-pre-line italic" data-testid="banter-text">
                  &ldquo;{banter}&rdquo;
                </p>
              ) : banterLoading ? (
                <p className="text-sm text-[#0F2537]/55 italic" data-testid="banter-loading">
                  CHIP is wringing the salt out of his shorts… one sec.
                </p>
              ) : (
                <p className="text-sm text-[#0F2537]/65 italic">
                  No banter yet. Tap below to make CHIP weigh in.
                </p>
              )}
              <div className="mt-4 flex items-center justify-between gap-2 text-[10px] text-[#0F2537]/55 font-bold uppercase tracking-wider border-t border-[#0F2537]/10 pt-3">
                <span>{r.course_name} · {r.tees} tees · {r.total_strokes} strokes</span>
                <span className="text-[#E05D3A]">bulagolf.app</span>
              </div>
            </div>
          </div>

          {/* Share row — only when there's banter to share */}
          {banter && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="banter-share-controls">
              <button
                type="button"
                disabled={banterShareBusy}
                data-testid="banter-share-png"
                onClick={async () => {
                  if (!banterCardRef.current) return;
                  setBanterShareBusy(true);
                  try {
                    const dataUrl = await toPng(banterCardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0F2537" });
                    const a = document.createElement("a");
                    a.href = dataUrl;
                    a.download = `bulagolf-banter-${id.slice(0, 6)}.png`;
                    a.click();
                    toast.success("Banter saved as PNG");
                  } catch { toast.error("Couldn't render image"); }
                  finally { setBanterShareBusy(false); }
                }}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90 transition disabled:opacity-60"
              >
                <Download size={14} weight="bold" /> {banterShareBusy ? "Saving…" : "Save PNG"}
              </button>
              <button
                type="button"
                data-testid="banter-share-native"
                onClick={async () => {
                  const text = `${banter}\n\n— Bula Banter, my ${r.total_strokes} at ${r.course_name}`;
                  if (navigator.share) {
                    try { await navigator.share({ title: "Bula Banter", text, url: window.location.origin + "/" }); }
                    catch { /* cancelled */ }
                  } else {
                    await navigator.clipboard.writeText(`${text} ${window.location.origin}/`);
                    toast.success("Banter copied to clipboard");
                  }
                }}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold hover:brightness-110 transition"
              >
                <ShareNetwork size={14} weight="fill" /> Share
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${banter}\n\n— Bula Banter, my ${r.total_strokes} at ${r.course_name}. ${window.location.origin}/`)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="banter-share-whatsapp"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-xs font-bold hover:border-[#0F2537]/40 transition"
              >
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${banter}\n\n— Bula Banter, my ${r.total_strokes} at ${r.course_name}`)}&url=${encodeURIComponent(window.location.origin + "/")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="banter-share-x"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-xs font-bold hover:border-[#0F2537]/40 transition"
              >
                <XLogo size={14} weight="fill" /> Post
              </a>
            </div>
          )}

          {/* Manual regenerate / fetch button — visible if banter is missing AND we didn't auto-load it (or auto-load failed) */}
          {!banter && !banterLoading && (
            <button
              type="button"
              data-testid="generate-banter-btn"
              onClick={async () => {
                setBanterLoading(true);
                try {
                  const res = await api.post(`/rounds/${id}/banter`);
                  setBanter(res.data.banter);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Banter generator is taking a nap. Try again.");
                } finally { setBanterLoading(false); }
              }}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition"
            >
              <Sparkle weight="fill" size={14} /> Generate banter
            </button>
          )}
        </div>
      )}

      <div className="card-default p-5 overflow-x-auto" data-testid="round-scorecard">
        <span className="label-eyebrow">Scorecard</span>
        <h2 className="font-heading font-bold text-xl mt-1 mb-4">Hole-by-hole</h2>
        <Nine label="Front 9" holes={front} sr={sr.slice(0,9)} sf={sf.slice(0,9)} hasHandicap={hasHandicap}
          pTot={sum(front, "par")} sTot={sum(front, "strokes")} sfTot={sf.slice(0,9).reduce((a,b)=>a+b,0)} />
        <div className="h-3" />
        <Nine label="Back 9" holes={back} sr={sr.slice(9,18)} sf={sf.slice(9,18)} hasHandicap={hasHandicap}
          pTot={sum(back, "par")} sTot={sum(back, "strokes")} sfTot={sf.slice(9,18).reduce((a,b)=>a+b,0)} />
      </div>

      <Link to="/rounds" className="text-sm text-[#0F2537]/60 hover:text-[#E05D3A]">← All rounds</Link>

      {/* Shot tracker map — Pro only, owner only */}
      {isOwner && (
        <ShotsSection
          isPro={!!user?.is_pro}
          holesWithShots={holesWithShots}
          shotsByHole={shotsByHole}
          course={course}
        />
      )}
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div className={`card-default p-5 ${accent ? "bg-[#0F2537] text-white border-[#0F2537]" : ""}`}>
      <span className={`label-eyebrow ${accent ? "!text-white/70" : ""}`}>{label}</span>
      <div className="font-heading font-black text-3xl mt-1">{v}</div>
    </div>
  );
}

function Nine({ label, holes, sr, sf, hasHandicap, pTot, sTot, sfTot }) {  return (
    <div>
      <div className="text-xs font-bold text-[#0F2537]/60 mb-2">{label}</div>
      <table className="w-full text-sm border-separate border-spacing-y-1 min-w-[720px]">
        <thead>
          <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest">
            <th className="text-left px-2 py-1">Hole</th>
            {holes.map((h) => <th key={h.number} className="px-2 py-1">{h.number}</th>)}
            <th className="px-2 py-1">Tot</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-[#F8F7F3]"><td className="px-2 py-1.5 font-semibold rounded-l-lg">Par</td>
            {holes.map((h) => <td key={h.number} className="text-center px-2 py-1.5">{h.par}</td>)}
            <td className="text-center font-bold px-2 py-1.5 rounded-r-lg">{pTot}</td>
          </tr>
          <tr><td className="px-2 py-1 font-semibold">Score</td>
            {holes.map((h) => {
              const d = h.strokes - h.par;
              const cls = d < 0 ? "text-[#E05D3A] font-bold" : d > 1 ? "text-[#0F2537]/60" : "text-[#0F2537]";
              return <td key={h.number} className={`text-center px-2 py-1 ${cls}`}>{h.strokes}</td>;
            })}
            <td className="text-center font-bold px-2 py-1">{sTot}</td>
          </tr>
          {hasHandicap && (
            <>
              <tr className="bg-[#F8F7F3]"><td className="px-2 py-1.5 font-semibold rounded-l-lg">Strokes</td>
                {sr.map((s, i) => <td key={i} className="text-center px-2 py-1.5">{s > 0 ? "●".repeat(s) : ""}</td>)}
                <td className="rounded-r-lg" />
              </tr>
              <tr><td className="px-2 py-1 font-semibold">Net</td>
                {holes.map((h, i) => <td key={h.number} className="text-center px-2 py-1 font-semibold">{h.strokes - (sr[i] || 0)}</td>)}
                <td className="text-center font-bold px-2 py-1">{holes.reduce((a,h,i) => a + h.strokes - (sr[i]||0), 0)}</td>
              </tr>
              <tr className="bg-[#F8F7F3]"><td className="px-2 py-1.5 font-semibold rounded-l-lg">Sf pts</td>
                {sf.map((p, i) => <td key={i} className="text-center px-2 py-1.5 font-bold text-[#E05D3A]">{p}</td>)}
                <td className="text-center font-bold px-2 py-1.5 rounded-r-lg">{sfTot}</td>
              </tr>
            </>
          )}
          <tr><td className="px-2 py-1 font-semibold">Putts</td>
            {holes.map((h) => <td key={h.number} className="text-center px-2 py-1">{h.putts ?? "—"}</td>)}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}


function ShotsSection({ isPro, holesWithShots, shotsByHole, course }) {
  const [openHole, setOpenHole] = useState(null);
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (holesWithShots.length && openHole == null) setOpenHole(holesWithShots[0]);
  }, [holesWithShots, openHole]);

  if (!isPro) {
    return (
      <div className="card-default p-5 flex items-center justify-between gap-3" data-testid="shotmap-paywall">
        <div className="flex items-center gap-2">
          <Crown size={16} weight="fill" className="text-[#E05D3A]" />
          <span className="text-sm font-bold">Shot maps + Club Bag are part of Bula Pro</span>
        </div>
        <Link to="/pricing" data-testid="shotmap-go-pro" className="px-3 py-1.5 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
          See pricing
        </Link>
      </div>
    );
  }

  if (!holesWithShots.length) {
    return (
      <div className="card-default p-5" data-testid="shotmap-empty">
        <div className="flex items-center gap-2 mb-1">
          <Target size={16} weight="fill" className="text-[#E05D3A]" />
          <span className="label-eyebrow">Shot map</span>
        </div>
        <p className="text-sm text-[#0F2537]/60">No shots tracked for this round. Open Score Entry on your next round to log shots and unlock per-hole maps.</p>
      </div>
    );
  }

  const holeShots = shotsByHole[openHole] || [];
  const courseHole = course?.holes?.find((h) => h.number === openHole);
  const green = courseHole?.green_lat != null && courseHole?.green_lng != null
    ? { lat: courseHole.green_lat, lng: courseHole.green_lng }
    : null;
  // Tee position: prefer admin-saved tee, otherwise back-project along the
  // green-front/back axis using the Blue tee yardage (same heuristic as the
  // CourseFlyover page so views look consistent).
  const tee = (() => {
    if (!courseHole) return null;
    if (courseHole.tee_lat != null && courseHole.tee_lng != null) {
      return { lat: courseHole.tee_lat, lng: courseHole.tee_lng };
    }
    if (!green || !course) return null;
    const blue = course.tees?.find((t) => t.name === "Blue") || course.tees?.[0];
    const yards = blue?.yards?.[openHole - 1] || 360;
    let dirLat = 1, dirLng = 0;
    if (courseHole.green_front_lat != null && courseHole.green_back_lat != null) {
      dirLat = courseHole.green_front_lat - courseHole.green_back_lat;
      dirLng = courseHole.green_front_lng - courseHole.green_back_lng;
    }
    const mag = Math.sqrt(dirLat * dirLat + dirLng * dirLng) || 1;
    const ux = dirLat / mag, uy = dirLng / mag;
    const dy = (yards * 0.9144) / 111000;
    const dx = (yards * 0.9144) / (111000 * Math.cos((green.lat * Math.PI) / 180));
    return { lat: green.lat + ux * dy, lng: green.lng + uy * dx };
  })();
  const totalYards = holeShots.reduce((a, s) => a + (s.distance_yards || 0), 0);
  const longest = holeShots.reduce((best, s) =>
    s.distance_yards != null && s.distance_yards > (best?.distance_yards || 0) ? s : best, null);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      // Wait a beat so any in-flight Leaflet tile loads paint into the canvas.
      await new Promise((r) => setTimeout(r, 600));
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#F8F7F3" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `bulagolf-hole${openHole}-shotmap.png`;
      a.click();
      toast.success("Shot map saved");
    } catch {
      toast.error("Could not generate image");
    } finally { setBusy(false); }
  };

  const shareText = longest
    ? `${longest.distance_yards} yds with ${longest.club} on hole ${openHole} at ${course?.name || "Bula Golf"} 🏌️`
    : `My shot map · hole ${openHole} at ${course?.name || "Bula Golf"} 🏌️`;
  const shareUrl = window.location.origin + "/";
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Bula Golf", text: shareText, url: shareUrl }); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Copied to clipboard");
    }
  };

  return (
    <div className="card-default p-5 space-y-4" data-testid="shotmap-section">
      <div className="flex items-center gap-2 flex-wrap">
        <Target size={16} weight="fill" className="text-[#E05D3A]" />
        <span className="label-eyebrow">Shot map</span>
        <span className="ml-auto text-xs font-bold text-[#0F2537]/60">{holesWithShots.length} hole{holesWithShots.length === 1 ? "" : "s"} tracked</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="shotmap-hole-tabs">
        {holesWithShots.map((n) => (
          <button
            key={n} onClick={() => setOpenHole(n)} data-testid={`shotmap-hole-${n}`}
            className={`w-9 h-9 rounded-lg text-xs font-bold transition ${
              openHole === n ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]/70 hover:border-[#0F2537]"
            }`}
          >{n}</button>
        ))}
      </div>

      {/* Capture target — everything inside cardRef becomes the PNG */}
      <div ref={cardRef} className="bg-[#F8F7F3] rounded-2xl p-4 md:p-5 border border-[#E2E0D8] space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-black text-xl">Hole {openHole}{courseHole ? ` · Par ${courseHole.par}` : ""}</div>
            <div className="text-xs font-bold text-[#0F2537]/60">{course?.name}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/50">Total</div>
            <div className="font-heading font-black text-xl">{totalYards > 0 ? `${totalYards} yds` : "—"}</div>
          </div>
        </div>
        <ShotMap shots={holeShots} green={green} tee={tee} holeNumber={openHole} />
        <ol className="space-y-1.5">
          {holeShots.map((s) => (
            <li key={s.id} data-testid={`shotmap-shot-${s.id}`} className="flex items-center gap-2 text-sm">
              <span className="w-6 h-6 rounded-full bg-[#E05D3A] text-white text-xs font-bold flex items-center justify-center shrink-0">
                {s.shot_index}
              </span>
              <span className="font-bold flex-1 truncate">{s.club}</span>
              {s.distance_yards != null ? (
                <span className="text-xs font-bold text-[#1E5631] bg-[#1E5631]/10 px-2 py-0.5 rounded-full">
                  {s.distance_yards} yds
                </span>
              ) : (
                <span className="text-xs text-[#0F2537]/50">no distance</span>
              )}
            </li>
          ))}
        </ol>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/40 text-right pt-1">Bula Golf · Fiji</div>
      </div>

      {/* Share controls */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1" data-testid="shotmap-share-controls">
        <button
          onClick={downloadCard} disabled={busy}
          data-testid="shotmap-download"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#0F2537] text-white text-sm font-bold disabled:opacity-50 hover:bg-[#0F2537]/90 transition"
        >
          <Download size={14} weight="bold" /> {busy ? "Saving…" : "Save PNG"}
        </button>
        <button
          onClick={nativeShare}
          data-testid="shotmap-share-native"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition"
        >
          <ShareNetwork size={14} weight="bold" /> Share
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
          target="_blank" rel="noreferrer"
          data-testid="shotmap-share-whatsapp"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#25D366] text-white text-sm font-bold hover:brightness-110 transition"
        >
          <WhatsappLogo size={14} weight="fill" /> WhatsApp
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank" rel="noreferrer"
          data-testid="shotmap-share-x"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-black text-white text-sm font-bold hover:bg-[#0F2537] transition"
        >
          <XLogo size={14} weight="fill" /> Post
        </a>
      </div>
    </div>
  );
}
